import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ClubCard from "../components/ClubCard";
import { Search, X } from "lucide-react";
import PageMeta from "../components/PageMeta";

const SPORTS = ["Football", "Basketball", "Rugby", "Handball", "Volleyball", "Tennis", "Athlétisme", "Hockey"];
const LEVELS = ["Loisir", "Départemental", "Régional", "National"];
const AGE_CATS = ["U7", "U9", "U11", "U13", "U15", "U17", "U18", "Senior", "Vétéran"];
const COLORS = ["Bleu", "Blanc", "Rouge", "Noir", "Vert", "Jaune", "Orange", "Violet", "Rose", "Gris"];

export default function Clubs() {
    const [searchParams] = useSearchParams();
    // Initialize from ?q=... (Google sitelinks search box / external links)
    const [filters, setFilters] = useState(() => {
        const q = searchParams.get("q");
        return q ? { q } : {};
    });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchClubs = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/clubs", { params: clean(filters) });
            setItems(data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClubs();
        // eslint-disable-next-line
    }, [filters]);

    const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v || undefined }));
    const reset = () => setFilters({});

    const activeCount = useMemo(() => Object.values(filters).filter(Boolean).length, [filters]);

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
            <PageMeta
                title="Clubs à sponsoriser — Sponsormania.fr | Marketplace sportif"
                description="Parcourez la marketplace de clubs sportifs amateurs cherchant des sponsors. Filtrez par sport, niveau, département, catégorie d'âge. Service 100% gratuit."
            />
            <div className="mb-10">
                <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                    Marketplace
                </div>
                <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
                    Trouvez un club à sponsoriser
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* FILTERS SIDEBAR */}
                <aside className="lg:col-span-3 space-y-6">
                    <div className="card-tu sticky top-20">
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold">
                                Filtres {activeCount > 0 && <span className="text-orange-500">({activeCount})</span>}
                            </div>
                            {activeCount > 0 && (
                                <button
                                    onClick={reset}
                                    data-testid="filter-reset"
                                    className="text-xs text-zinc-400 hover:text-orange-500 inline-flex items-center gap-1"
                                >
                                    <X size={12} /> Reset
                                </button>
                            )}
                        </div>

                        <div className="mb-4 relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                            <input
                                data-testid="filter-search"
                                placeholder="Rechercher…"
                                value={filters.q || ""}
                                onChange={(e) => setF("q", e.target.value)}
                                className="input-tu pl-9"
                            />
                        </div>

                        <FilterBlock label="Sport">
                            <select
                                data-testid="filter-sport"
                                className="input-tu"
                                value={filters.sport || ""}
                                onChange={(e) => setF("sport", e.target.value)}
                            >
                                <option value="">Tous</option>
                                {SPORTS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </FilterBlock>

                        <FilterBlock label="Département">
                            <input
                                data-testid="filter-department"
                                placeholder="Ex. 69 - Rhône"
                                value={filters.department || ""}
                                onChange={(e) => setF("department", e.target.value)}
                                className="input-tu"
                            />
                        </FilterBlock>

                        <FilterBlock label="Ville">
                            <input
                                data-testid="filter-city"
                                placeholder="Ville"
                                value={filters.city || ""}
                                onChange={(e) => setF("city", e.target.value)}
                                className="input-tu"
                            />
                        </FilterBlock>

                        <FilterBlock label="Catégorie d'âge">
                            <select
                                data-testid="filter-age"
                                className="input-tu"
                                value={filters.age_category || ""}
                                onChange={(e) => setF("age_category", e.target.value)}
                            >
                                <option value="">Toutes</option>
                                {AGE_CATS.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </FilterBlock>

                        <FilterBlock label="Niveau">
                            <select
                                data-testid="filter-level"
                                className="input-tu"
                                value={filters.level || ""}
                                onChange={(e) => setF("level", e.target.value)}
                            >
                                <option value="">Tous</option>
                                {LEVELS.map((l) => (
                                    <option key={l} value={l}>
                                        {l}
                                    </option>
                                ))}
                            </select>
                        </FilterBlock>

                        <FilterBlock label="Couleur dominante">
                            <div className="flex flex-wrap gap-2">
                                {COLORS.map((c) => {
                                    const active = filters.color === c;
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            data-testid={`filter-color-${c}`}
                                            onClick={() => setF("color", active ? "" : c)}
                                            className={[
                                                "px-3 py-1 text-[11px] uppercase tracking-wider border transition-colors",
                                                active
                                                    ? "border-orange-500 text-orange-500 bg-orange-500/10"
                                                    : "border-zinc-800 text-zinc-300 hover:border-zinc-600",
                                            ].join(" ")}
                                        >
                                            {c}
                                        </button>
                                    );
                                })}
                            </div>
                        </FilterBlock>
                    </div>
                </aside>

                {/* LIST */}
                <div className="lg:col-span-9">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-4">
                        {loading ? "Chargement…" : `${items.length} club${items.length > 1 ? "s" : ""} trouvé${items.length > 1 ? "s" : ""}`}
                    </div>
                    {items.length === 0 && !loading ? (
                        <div className="card-tu text-center text-zinc-400 py-16">
                            Aucun club ne correspond à vos critères.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {items.map((c) => (
                                <ClubCard key={c.id} club={c} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function FilterBlock({ label, children }) {
    return (
        <div className="mb-4">
            <label className="label-tu">{label}</label>
            {children}
        </div>
    );
}

function clean(o) {
    return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== ""));
}
